import React, { useState, useEffect } from "react";
import { DatePicker, Checkbox, Card } from "antd";
import dayjs from "dayjs";
import Table from "../../core/pagination/datatable";
import { getProductOrderPrediction } from "../../core/api/mediaplan";
import { useSelector, shallowEqual } from "react-redux";
const { RangePicker } = DatePicker;
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
dayjs.extend(isSameOrBefore);
import { get100DaysROR } from "../../core/api/report";
import useOncePerKeyEffect from "../../hooks/useOncePerKeyEffect";

export default function ProductOrderPrediction() {
  const selectedClients = useSelector(
    (state) => state.selectedClients,
    shallowEqual
  );
  const [data, setData] = useState([]);
  const [dateRange, setDateRange] = useState([
    dayjs(),
    dayjs().add(1, "month"),
  ]);

  const [columns, setColumns] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [rorValues, setRorValues] = useState({});
  const [loading, setLoading] = useState(false);
  // Using reusable hook to avoid duplicate fetches under React 18 StrictMode

  const generateDataSource = (data,values) => {
    const rows = data.map((item, index) => {
      const baseRow = {
        key: index,
        brand: item.brand || "",
        mailDate: item.mail_date || "",
        productName: item.product_name || "",
        strategy: item.strategy || "",
        confirmed: false,
        campaignCode: item.campaign_code || "",
        printer: item.printer || "",
        offerCode: item.offer_code || "",
        targetMailQty: item.target_mail_qty || 0,
        actualMailQty: item.actual_mail_qty || 0,
        category: item.category || "",
        estimatedRoR: item.estimated_ror || 0,
        qtyPerOrder: item.qty_per_order || 0,
      };

      let current = dateRange[0];
      const mailDate = dayjs(item.mail_date);

      while (current.isSameOrBefore(dateRange[1], "day")) {
        const dateKey = current.format("YYYY-MM-DD");

        let dayDiff = current.diff(mailDate, "day");

        let rorValue = 0;
        if (dayDiff >= 0 && dayDiff <= 100) {
          rorValue = values[dayDiff] || 0;
        }

        const calculatedQty =
          (((item.target_mail_qty || 0) * (item.estimated_ror || 0)) / 100) *
          (rorValue / 100);

        baseRow[dateKey] = parseFloat(calculatedQty.toFixed(2));
        current = current.add(1, "day");
      }
console.log(baseRow, "baseRow")
      return baseRow;
    });

    setDataSource(rows);
  };

  useOncePerKeyEffect(
    JSON.stringify(selectedClients || []),
    () => {
      let aborted = false;
      const loadData = async () => {
        setLoading(true);
        try {
          const ror = await get100DaysROR(selectedClients);
          if (aborted) return;
          const values = ror.data;
          setRorValues(values);
          const response = await getProductOrderPrediction(selectedClients);
          if (aborted) return;
          console.log(response, "response1");
          setData(response.data);
          generateDataSource(response.data, values);
        } finally {
          if (!aborted) setLoading(false);
        }
      };
      loadData();
      return () => {
        aborted = true;
      };
    },
    [selectedClients]
  );

  useEffect(() => {
    generateColumns();
    generateDataSource(data,rorValues);
  }, [dateRange]);
  const generateColumns = () => {
    const fixedColumns = [
      {
        title: "Brand",
        dataIndex: "brand",
        key: "brand",
        fixed: "left",
        width: 90,
      },
      {
        title: "Mail Date",
        dataIndex: "mailDate",
        key: "mailDate",
        fixed: "left",
        width: 90,
      },
      {
        title: "Product Name",
        dataIndex: "productName",
        key: "productName",
        fixed: "left",
        width: 300,
      },
      {
        title: "Strategy",
        dataIndex: "strategy",
        key: "strategy",
        fixed: "left",
        width: 90,
      },
      {
        title: "Confirmed",
        dataIndex: "confirmed",
        key: "confirmed",
        render: (_, record) => (
          <Checkbox
            checked={record.confirmed}
            onChange={(e) => handleCheckboxChange(e.target.checked, record.key)}
          />
        ),
        width: 90,
      },
      {
        title: "Campaign Code",
        dataIndex: "campaignCode",
        key: "campaignCode",
        fixed: "left",
        width: 170,
      },
      { title: "Printer", dataIndex: "printer", key: "printer", width: 70 },
      { title: "Offer Code", dataIndex: "offerCode", key: "offerCode" },
      {
        title: "Qty per Order",
        dataIndex: "qtyPerOrder",
        key: "qtyPerOrder",
        width: 80,
      },

      {
        title: "Target Mail Qty",
        dataIndex: "targetMailQty",
        key: "targetMailQty",
        width: 100,
      },
      {
        title: "Actual Mail Qty",
        dataIndex: "actualMailQty",
        key: "actualMailQty",
        width: 100,
      },
      { title: "Category", dataIndex: "category", key: "category", width: 90 },
      {
        title: "Estimated RoR %",
        dataIndex: "estimatedRoR",
        key: "estimatedRoR",
        render: (text) => <span>{text}%</span>,
        width: 100,
      },
    ];

    const dynamicColumns = [];
    let current = dateRange[0];
    while (current.isSameOrBefore(dateRange[1], "day")) {
      dynamicColumns.push({
        title: current.format("YYYY-MM-DD"),
        dataIndex: current.format("YYYY-MM-DD"),
        key: current.format("YYYY-MM-DD"),

        width: 100,
      });
      current = current.add(1, "day");
    }

    setColumns([...fixedColumns, ...dynamicColumns]);
  };

  const handleCheckboxChange = (checked, key) => {
    setDataSource((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, confirmed: checked } : item
      )
    );
  };

  return (
    <Card>
      <div className="d-flex items-center">
        <label className="col-1 text-lg font-semibold whitespace-nowrap">
          Date Range:
        </label>
        <RangePicker
          style={{ width: 575 }}
          value={dateRange}
          onChange={(dates) => dates && setDateRange(dates)}
          allowClear={false}
        />
      </div>

      <Table columns={columns} dataSource={dataSource} loading={loading} />
    </Card>
  );
}
